<template>
	<div class="pc-email-container">
		<n-form ref="formRef" :model="formValue" :rules="rules">
			<n-form-item path="userEmail">
				<n-auto-complete
					v-model:value="formValue.userEmail"
					:input-props="{
						autocomplete: 'disabled',
					}"
					:options="options"
					:placeholder="copy.placeholderEmail"
					clearable
				/>
			</n-form-item>
			<n-form-item path="userCode" class="code-box">
				<n-input
					v-model:value="formValue.userCode"
					:placeholder="copy.placeholderCode"
					:maxlength="6"
				/>
				<p class="code-btn">
					<span v-if="countdown <= 0" @click="getCodeHandle">{{ copy.getCode }}</span>
					<span v-else>{{ countdown }}{{ copy.countdownUnit }}</span>
				</p>
			</n-form-item>

			<!-- 同意协议 -->
			<Agree v-model="isAgree" :locale="locale" />
			<n-form-item>
				<div :class="isAgree ? 'submit-btn' : 'submit-btn no-submit'" @click="submitForm">
					{{ btnText }}
				</div>
			</n-form-item>
		</n-form>
	</div>
</template>

<script setup>
import useLogin from "@/hooks/login/index";
import { useUserStore } from "~/stores/user";
import { emailSuffixes } from "~/utils/constants";
import EmailValidator from "email-validator";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
// 组件
import Agree from "./pc-agree.vue";

const emits = defineEmits("loginSuccess");
const props = defineProps({
	btnText: {
		type: String,
		default: "登入/註冊",
	},
	loginType: {
		type: String,
		default: "email",
	},
	/** 合并进 /v1/login/app 的额外入参（導師允許登錄 v=2） */
	loginExtraParams: {
		type: Object,
		default: () => ({}),
	},
	/** UI 語言：`zh-TW`（預設）| `en` */
	locale: {
		type: String,
		default: "zh-TW",
	},
});

const copy = computed(() =>
	props.locale === "en"
		? {
				placeholderEmail: "Email address",
				placeholderCode: "Verification code",
				getCode: "Get code",
				countdownUnit: "s",
				errEmailRequired: "Please enter your email",
				errEmailFormat: "Invalid email format",
				errCodeRequired: "Please enter the verification code",
				errCodeDigits: "Please enter the 6-digit code",
			}
		: {
				placeholderEmail: "請輸入郵箱",
				placeholderCode: "請輸入驗證碼",
				getCode: "獲取驗證碼",
				countdownUnit: "s",
				errEmailRequired: "請輸入郵箱",
				errEmailFormat: "郵箱格式錯誤",
				errCodeRequired: "請輸入驗證碼",
				errCodeDigits: "請輸入6位數字驗證碼",
			}
);

const isAgree = ref(true); // 同意协议
// 表单
const formRef = ref(null);
const formValue = ref({
	userEmail: "",
	userCode: "",
});
// 表单校验规则（随 locale 变化）
const rules = computed(() => ({
	userEmail: [
		{
			required: true,
			trigger: ["input", "blur"],
			validator(rule, value) {
				if (!value) {
					return new Error(copy.value.errEmailRequired);
				} else if (!EmailValidator.validate(value)) {
					return new Error(copy.value.errEmailFormat);
				} else {
					return true;
				}
			},
		},
	],
	userCode: [
		{
			required: true,
			trigger: ["input", "blur"],
			validator(rule, value) {
				if (!value) {
					return new Error(copy.value.errCodeRequired);
				} else if (!/^[0-9]\d{5}$/.test(value)) {
					return new Error(copy.value.errCodeDigits);
				} else {
					return true;
				}
			},
		},
	],
}));
// 邮箱后缀
const options = computed(() => {
	return emailSuffixes.map(suffix => {
		const inputValue = formValue.value.userEmail?.split("@")[0];
		return {
			label: inputValue + suffix,
			value: inputValue + suffix,
		};
	});
});

// 登录业务
const { validEmail, countdown, getCode, handleLogin } = useLogin();

// 获取验证码
const getCodeHandle = () => {
	if (!validEmail(formValue.value.userEmail)) {
		return false;
	}
	const codeParams = {
		email: formValue.value.userEmail,
		type: "LOGIN",
	};
	getCode(codeParams);
};

// 提交登录
const useUser = useUserStore();
const { thirdBindEmailParams } = storeToRefs(useUser);
const submitForm = async e => {
	if (!isAgree.value) {
		return false;
	}
	e.preventDefault();
	formRef.value?.validate(errors => {
		if (!errors) {
			const { userEmail, userCode } = formValue.value;
			logEvent("site_login_button_click");

			// 第三方登录绑定邮箱操作
			if (props.loginType === "bind-email") {
				const { login_type, third_access_token, open_id } = thirdBindEmailParams.value;
				const thirdParams = {
					email: userEmail,
					code: userCode,
					login_type,
					third_access_token,
					...props.loginExtraParams,
				};
				if (open_id) {
					thirdParams.open_id = open_id;
				}
				handleLogin(thirdParams, () => emits("loginSuccess"));
			} else {
				// 邮箱登录
				const params = {
					email: userEmail,
					code: userCode,
					login_type: "email",
					...props.loginExtraParams,
				};
				handleLogin(params, () => emits("loginSuccess"));
			}
		}
	});
};
</script>

<style lang="scss" scoped>
.pc-email-container {
	color: #232323;

	.submit-btn {
		margin: 0 auto;
		width: 280px;
		height: 48px;
		font-family: Source Han Sans CN;
		font-weight: 500;
		font-size: 22px;
		color: #ffffff;
		line-height: 48px;
		text-align: center;
		background: linear-gradient(90deg, #ce8b4d, #bb712e);
		border-radius: 8px;
		cursor: pointer;
	}

	.no-submit {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.code-box {
		position: relative;
		.code-btn {
			position: absolute;
			right: 16px;
			top: 13px;
			font-size: 16px;
			color: #c8843c;
			cursor: pointer;
		}
	}
}

// 重写 Naive UI 样式
:deep(.n-form-item) {
	--n-label-height: 0 !important;
}
:deep(.n-input) {
	height: 48px !important;
	line-height: 48px !important;
	font-size: 16px;
	border-radius: 4px;
	border: 1px solid #c67f34;
	--n-border-focus: 1px solid #c67f34 !important;
	--n-border-hover: 1px solid #c67f34 !important;
	--n-box-shadow-focus: 0 0 0 0 rgba(200, 132, 60, 0.2) !important;
}
</style>
